import { useRef } from 'react';
import axios from 'axios';
import { ApiRequest } from '../types';

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

const {
  // REACT_APP_API_BASE_URL = 'https://api.staging.ayblehealth.com'
  REACT_APP_API_BASE_URL = 'http://localhost:8000',
} = process.env;

type Options = {
  throwIfError: boolean;
};

export class ApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

export const useApi = ({ throwIfError }: Options = { throwIfError: false }) => {
  const controllerRef = useRef(new AbortController());

  const cancel = () => {
    controllerRef.current.abort();
  };

  const api = async <PayloadType = undefined>(
    request: ApiRequest<PayloadType>
  ) => {
    const requestHeaders = request.headers ?? DEFAULT_HEADERS;
    const headers = request.token
      ? { ...requestHeaders, Authorization: `Bearer ${request.token.trim()}` }
      : requestHeaders;

    try {
      const requestData = {
        data: request.payload,
        headers,
        method: request.method || 'GET',
        responseType: request.responseType,
        signal: controllerRef.current.signal,
        url:
          request.url ||
          [REACT_APP_API_BASE_URL, request.route.replace(/^\/+/, '')].join('/'),
      };

      const response = await axios.request(requestData);
      return response.data;
    } catch (error: any) {
      console.error(error?.message || 'Unknown error');

      if (throwIfError) {
        let detail = error?.response?.data?.detail;

        // With responseType: 'blob', axios parses the error response body
        // as a Blob too, even for a JSON error - decode it before reading
        // `detail`.
        if (detail === undefined && error?.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            detail = JSON.parse(text)?.detail;
          } catch {
            // fall through to the generic message below
          }
        }

        throw new ApiError(
          detail ?? 'Error with unknown details.',
          error?.response?.status
        );
      }
    }

    return null;
  };

  return { api, cancel };
};
